// Header.jsx
import { routes } from "../config/routes";
import { useUser } from "../contexts/UserContext";
import { useNavigate } from "react-router-dom";
import React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  ButtonGroup,
  Button,
  Tooltip,
  Avatar,
  Divider,
  useMediaQuery,
  ListItemButton,
  Fade,
  Paper,
} from "@mui/material";
import { ChevronDown } from "lucide-react";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { useState } from "react";

export const Header = ({ setOpenMenu, openMenu }) => {
  return (
    <AppBar
      position="fixed"
      sx={{ backgroundColor: "#0563B0", boxShadow: "none" }}
    >
      <Toolbar>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <IconButton
            color="inherit"
            onClick={() => setOpenMenu(!openMenu)}
            size="large"
            edge="start"
          >
            {openMenu ? (
              <CloseIcon
                sx={{
                  width: 24,
                  height: 24,
                  animation: "fadeIn 0.3s ease-in",
                  "@keyframes fadeIn": {
                    "0%": {
                      opacity: 0,
                      transform: "rotate(-90deg)",
                    },
                    "100%": {
                      opacity: 1,
                      transform: "rotate(0deg)",
                    },
                  },
                }}
              />
            ) : (
              <MenuIcon
                sx={{
                  width: 24,
                  height: 24,
                  animation: "fadeIn 0.3s ease-in",
                  "@keyframes fadeIn": {
                    "0%": {
                      opacity: 0,
                      transform: "rotate(90deg)",
                    },
                    "100%": {
                      opacity: 1,
                      transform: "rotate(0deg)",
                    },
                  },
                }}
              />
            )}
          </IconButton>
          <img src="/Logo.png" alt="Logo" style={{ width: "100px" }} />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export const SideMenu = ({ openMenu, setOpenMenu }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { instance } = useMsal();
  const { user, setUser } = useUser();
  const activeAccount = instance?.getActiveAccount();
  const [logoutMenuAnchorEl, setLogoutMenuAnchorEl] = useState(null);
  
  const handleNavigation = async (route) => {
    if (typeof route === 'object' && route.isExternal) {
      const url = route.externalUrl;
      if (route.id === 'research-monitor') {
        try {

          const result = await instance.acquireTokenSilent({
            scopes: ["api://7cee488c-92df-4ca8-8f52-52b8ecce6ffa/InsidePortal.Read"],
            account: instance.getActiveAccount(),
          });
          
          const isManager = user.roles?.some(r => r.ResearchMonitorRole === 'manager') ?? false;
          const userRole = user.roles?.find(r => r.ResearchMonitorRole)?.ResearchMonitorRole || 'none';
          const isAdmin = user.roles?.some(r => r.admin === true) ?? false;
          
          // Store in sessionStorage as fallback (external app checks this)
          sessionStorage.setItem('token', result.accessToken);
          sessionStorage.setItem('isManager', String(isManager));
          
          
          // Also navigate with token in URL params
          const separator = url.includes('?') ? '&' : '?';
          const finalUrl = `${url}${separator}token=${encodeURIComponent(result.accessToken)}&isManager=${isManager}`;
          
          
          window.location.href = finalUrl;
        } catch (error) {
          console.error('Failed to acquire token for Research Monitor', error);
          window.location.href = url;
        }
        setOpenMenu(false);
        return;
      }
      
      if (route.id === 'fuel-control') {
        try {
        
          const result = await instance.acquireTokenSilent({
            scopes: ["api://7cee488c-92df-4ca8-8f52-52b8ecce6ffa/InsidePortal.Read"],
            account: instance.getActiveAccount(),
          });
          
          const email = activeAccount?.username || '';
          
          // Store in sessionStorage as fallback
          sessionStorage.setItem('token', result.accessToken);
          
          // Navigate with token in URL params
          const separator = url.includes('?') ? '&' : '?';
          const finalUrl = `${url}${separator}token=${encodeURIComponent(result.accessToken)}&email=${encodeURIComponent(email)}`;

          
          window.location.href = finalUrl;
        } catch (error) {
          console.error(' Failed to acquire token for Fuel Control', error);
          window.location.href = url;
        }
        setOpenMenu(false);
        return;
      }
      
      if (route.id === 'HR-CV-Matching') {
        try {
          const result = await instance.acquireTokenSilent({
            scopes: ["api://7cee488c-92df-4ca8-8f52-52b8ecce6ffa/InsidePortal.Read"],
            account: instance.getActiveAccount(),
          });

          const email = activeAccount?.username || '';

          sessionStorage.setItem('token', result.accessToken);

          const separator = url.includes('?') ? '&' : '?';
          const finalUrl = `${url}${separator}token=${encodeURIComponent(result.accessToken)}&email=${encodeURIComponent(email)}`;

          window.location.href = finalUrl;
        } catch (error) {
          console.error('Failed to acquire token for HR CV Matching', error);
          window.location.href = url;
        }
        setOpenMenu(false);
        return;
      }

      if (route.openInNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = url;
      }
      setOpenMenu(false);
      return;
    }
    
    const path = typeof route === 'string' ? route : route.path;
    navigate(path);
    setOpenMenu(false);
  };

  const handleLoginRedirect = async () => {
    await instance.loginRedirect({
      redirectUri: "/redirect",
    });
  };

  const handleLogoutRedirect = () => {
    setLogoutMenuAnchorEl(null);
    localStorage.removeItem("qrCodeScannedItems"); // Clear QR code scanned items
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
      account: activeAccount,
    });
  };

  return !isMobile ? (
    <Box
      sx={{
        width: "150",
        borderRight: "1px solid rgba(0, 0, 0, 0.12)",
        backgroundColor: "white",
        display: "flex",
        flexDirection: "column",
        minWidth: "150",
        position: "sticky",
        top: 0,
        height: "100vh",
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "sticky",
          top: 0,
        }}
      >
        <Box
          sx={{
            p: 2,
            backgroundColor: "#0563B0",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">תפריט</Typography>
        </Box>

        <AuthenticatedTemplate>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              backgroundColor: "#f8f9fa",
            }}
          >
            <Avatar sx={{ bgcolor: "#baa1d6" }}>
              {activeAccount?.name
                .split(" ")
                .map((part) => part[0])
                .join("")}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {activeAccount?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {activeAccount?.username}
              </Typography>
            </Box>
          </Box>

          <Divider />

          <List sx={{ flex: 1, p: 1, direction: "rtl" }}>
            {routes
              .filter(
                (page) =>
                  page.showInNav && (
                    user?.accessRights?.admin === true ||
                    (user?.accessRights && user?.accessRights[page.accessRight] === true)
                  )
              )
              .map((page) => (
                <ListItem
                  key={page.path || page.id}
                  button
                  onClick={() => handleNavigation(page)}
                  sx={{
                    ...(window.location.pathname === page.path
                      ? {
                          fontSize: "16px",
                          backgroundColor: "#f8f9fa",
                          borderBottom: "2px solid #fff",
                          borderRadius: 1,
                          mb: 0.5,
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }
                      : {
                          fontSize: "16px",
                          backgroundColor: "#fff",
                          borderBottom: "2px solid transparent",
                          borderRadius: 1,
                          mb: 0.5,
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }),
                  }}
                >
                  <ListItemText
                    primary={page.label}
                    sx={{
                      ...(window.location.pathname === page.path
                        ? {
                            //textAlign: "left",
                            "& .MuiTypography-root": {
                              color: "#0563B0",
                              fontWeight: "bold",
                            },
                          }
                        : {
                            //textAlign: "left",
                            "& .MuiTypography-root": {
                              color: "text.primary",
                            },
                          }),
                    }}
                  />
                </ListItem>
              ))}
            <ListItem
              button
              onClick={handleLogoutRedirect}
              sx={{
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: "hsla(4, 96.30%, 42.20%, 0.08)",
                },
              }}
            >
              <ListItemText
                primary="יציאה"
                sx={{
                  textAlign: "center",
                  border: "2px solid #ce0b0b",
                  padding: "8px 15px",
                  borderStyle: "solid",
                  borderRadius: "4px",
                  borderWidth: "1px",
                  "& .MuiTypography-root": {
                    color: "#f44336",
                  },
                }}
              />
            </ListItem>
          </List>

          <Divider />
          <List sx={{ p: 1 }}></List>
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <Box sx={{ p: 2 }}>
            <ListItem
              button
              onClick={handleLoginRedirect}
              sx={{
                borderRadius: 1,
                backgroundColor: "#0563B0",
                color: "white",
                "&:hover": {
                  backgroundColor: "#0454a0",
                },
              }}
            >
              <ListItemText primary="כניסה" sx={{ textAlign: "center" }} />
            </ListItem>
          </Box>
        </UnauthenticatedTemplate>
      </Box>
    </Box>
  ) : (
    <Drawer
      anchor="right"
      open={openMenu}
      onClose={() => setOpenMenu(false)}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          backgroundColor: "white",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            backgroundColor: "#0563B0",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">תפריט</Typography>
          <IconButton
            color="inherit"
            onClick={() => setOpenMenu(false)}
            sx={{ padding: 0 }}
          >
            <KeyboardBackspaceIcon />
          </IconButton>
        </Box>

        <AuthenticatedTemplate>
          {/* User Profile Section */}
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              backgroundColor: "#f8f9fa",
            }}
          >
            <Avatar sx={{ bgcolor: "#baa1d6" }}>
              {activeAccount?.name
                .split(" ")
                .map((part) => part[0])
                .join("")}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {activeAccount?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {activeAccount?.username}
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Navigation Menu */}
          <List sx={{ flex: 1, p: 1, direction: "rtl" }}>
            {routes
              .filter(item => item.showInNav && (
                user?.accessRights?.admin === true || 
                (user?.accessRights && user?.accessRights[item.accessRight] === true)
              ))
              .map((item) => (
                <ListItem
                  button
                  onClick={() => handleNavigation(item)}
                  key={item.path || item.id}
                  sx={{
                    ...(window.location.pathname === item.path
                      ? {
                          fontSize: "16px",
                          backgroundColor: "#f8f9fa",
                          borderBottom: "2px solid #fff",
                          borderRadius: 1,
                          mb: 0.5,
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }
                      : {
                          fontSize: "16px",
                          backgroundColor: "#fff",
                          borderBottom: "2px solid transparent",
                          borderRadius: 1,
                          mb: 0.5,
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }),
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    sx={{
                      ...(window.location.pathname === item.path
                        ? {
                            //textAlign: "left",
                            "& .MuiTypography-root": {
                              color: "#0563B0",
                              fontWeight: "bold",
                            },
                          }
                        : {
                            //textAlign: "left",
                            "& .MuiTypography-root": {
                              color: "text.primary",
                            },
                          }),
                    }}
                  />
                </ListItem>
              ))}
          </List>

          <Divider />

          {/* Logout Button */}
          <Box sx={{ p: 2 }}>
            <ListItemButton
              onClick={handleLogoutRedirect}
              sx={{
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: "hsla(4, 96.30%, 42.20%, 0.08)",
                },
              }}
            >
              <ListItemText
                primary="יציאה"
                sx={{
                  textAlign: "center",
                  border: "2px solid #ce0b0b",
                  padding: "8px 15px",
                  borderStyle: "solid",
                  borderRadius: "4px",
                  borderWidth: "1px",
                  "& .MuiTypography-root": {
                    color: "#f44336",
                  },
                }}
              />
            </ListItemButton>
          </Box>
        </AuthenticatedTemplate>

        <UnauthenticatedTemplate>
          <Box
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              height: "100%",
              justifyContent: "center",
            }}
          >
            <Fade in timeout={800}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  p: { xs: 4, sm: 5 },
                  mb: 4,
                  width: "100%",
                  maxWidth: "450px",
                  background: "#ffffff",
                  position: "relative",
                  border: "1px solid rgba(0,0,0,0.08)",
                  boxShadow: "0 8px 16px -2px rgba(0,0,0,0.1)",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    mb: 4,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    sx={{
                      fontSize: { xs: "1.75rem", sm: "2.5rem" },
                      mb: 2,
                      color: "#0563B0",
                    }}
                  >
                    ברוכים הבאים!
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.1rem" },
                      color: "#6b7280",
                      fontWeight: 400,
                      maxWidth: "300px",
                    }}
                  >
                    אנא התחבר לחשבון שלך על מנת להמשיך
                  </Typography>
                </Box>

                <Button
                  onClick={handleLoginRedirect}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: "#0563B0",
                    color: "white",
                    py: 1.5,
                    px: 4,
                    fontSize: "1.1rem",
                    fontWeight: 500,
                    width: "100%",
                    "&:hover": {
                      backgroundColor: "#0454a0",
                      boxShadow: "0 4px 8px rgba(5, 99, 176, 0.25)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  כניסה
                </Button>
              </Paper>
            </Fade>
          </Box>
        </UnauthenticatedTemplate>
      </Box>
    </Drawer>
  );
};
